import React, { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import MetisMenu from 'metismenujs';
import SimpleBar from 'simplebar-react';

import { checkHasPermission, onClickLink } from 'utils/permission';
import { LOCATIONS } from 'constants/index';

import { ReactComponent as HomeIconSVG } from 'assets/images/headerNavigation/bx-home-circle.svg';
import { ReactComponent as BriefCaseIconSVG } from 'assets/images/sideBar/far_fa-briefcase.svg';
import { ReactComponent as CalendarIconSVG } from 'assets/images/sideBar/bx-calendar.svg';
import { ReactComponent as UserIconSVG } from 'assets/images/sideBar/contact-round.svg';
import { ReactComponent as OperationIconSVG } from 'assets/images/sideBar/ClockCounterClockwise.svg';
import { ReactComponent as DiscoverSVG } from 'assets/images/sideBar/compass.svg';
import { ReactComponent as CustomerManagementIconSVG } from 'assets/images/sideBar/users-four.svg';
import { ReactComponent as GiftIconSVG } from 'assets/images/sideBar/gift.svg';
import { ReactComponent as StarIconSVG } from 'assets/images/sideBar/Star.svg';
import { ReactComponent as PercentIconSVG } from 'assets/images/sideBar/SealPercent.svg';
import { ReactComponent as HeartIconSVG } from 'assets/images/sideBar/heart.svg';
import { ReactComponent as ImagesIconSVG } from 'assets/images/sideBar/Images.svg';
import { ReactComponent as BankIconSVG } from 'assets/images/sideBar/bank.svg';
import { ReactComponent as HandCoinsIconSVG } from 'assets/images/HandCoins.svg';
import { ReactComponent as BookeseIconSVG } from 'assets/images/sideBar/bookese.svg';
import { ReactComponent as ComplaintIconSVG } from 'assets/images/sideBar/complaint.svg';
import { ReactComponent as TaxIconSVG } from 'assets/images/Percent-tab.svg';

import './styles.scss';
import ENDPOINT from 'constants/endPoint';

const getNormalizedPathname = (pathname) => {
  const pathParts = pathname.split('/');

  if (pathParts[1] === 'locations') {
    return LOCATIONS.LOCATION_LIST;
  }

  if (pathParts[1] === 'manager-gifts') {
    if (pathParts[2] === 'exchange') {
      return '/manager-gifts/exchange';
    }

    if (pathParts[2] === 'voucher-campaign' || pathParts[2] === 'voucher') {
      return '/manager-gifts/voucher';
    }

    return '/manager-gifts/list';
  }

  if (pathParts[1] === 'manager-complaints') {
    return LOCATIONS.MANAGER_COMPLAINT;
  }

  return pathname;
};

const resetMenuActiveState = () => {
  const ul = document.getElementById('side-menu');
  if (!ul) return;

  ul.querySelectorAll('a.active').forEach((element) => {
    element.classList.remove('active');
  });
  ul.querySelectorAll('.mm-active').forEach((element) => {
    element.classList.remove('mm-active');
  });
  ul.querySelectorAll('.mm-show').forEach((element) => {
    element.classList.remove('mm-show');
  });
  ul.querySelectorAll('.sidebar-popup-open').forEach((element) => {
    element.classList.remove('sidebar-popup-open');
  });
};

const findMatchingMenuItem = (pathname) => {
  const ul = document.getElementById('side-menu');
  if (!ul) return null;

  const items = ul.getElementsByTagName('a');
  const pathName = getNormalizedPathname(pathname);
  const route = `/${pathName.split('/')[1]}`;

  for (let i = 0; i < items.length; i += 1) {
    if (pathName === items[i].pathname || route === items[i].pathname) {
      return items[i];
    }
  }

  return null;
};

const SidebarContent = (props) => {
  const { locationList, placeDetail, myPermission } = props;

  const containerRef = React.useRef();
  const location = useLocation();
  const locationId = React.useMemo(() => {
    if (!location.pathname.startsWith('/locations')) return null;

    return new URLSearchParams(location.search).get('place');
  }, [location.pathname, location.search]);
  const currentLocation = React.useMemo(() => {
    const locationFromList = locationList.find((item) => item._id === locationId);

    if (locationFromList) {
      return locationFromList;
    }

    if (placeDetail?._id === locationId) {
      return placeDetail;
    }

    return null;
  }, [locationId, locationList, placeDetail]);

  const onScrollElement = React.useCallback((item) => {
    if (item) {
      const currentPosition = item.offsetTop;

      if (currentPosition > window.innerHeight) {
        containerRef.current.getScrollElement().scrollTop = currentPosition - 300;
      }
    }
  }, []);

  const onActivateParentDropdown = React.useCallback((item) => {
    item.classList.add('active');

    const parent = item.parentElement;
    const parent2El = parent.childNodes[1];

    if (parent2El && parent2El.id !== 'side-menu') {
      parent2El.classList.add('mm-show');
    }

    if (parent) {
      parent.classList.add('mm-active');
      const parent2 = parent.parentElement;

      if (parent2) {
        parent2.classList.add('mm-show'); // ul tag
        const parent3 = parent2.parentElement; // li tag

        if (parent3) {
          parent3.classList.add('mm-active'); // li
          parent3.childNodes[0].classList.add('mm-active'); // a
          const parent4 = parent3.parentElement; // ul

          if (parent4) {
            parent4.classList.add('mm-show'); // ul
            const parent5 = parent4.parentElement;

            if (parent5) {
              parent5.classList.add('mm-show'); // li
              parent5.childNodes[0].classList.add('mm-active'); // a tag
            }
          }
        }
      }

      onScrollElement(item);

      return false;
    }

    onScrollElement(item);

    return false;
  }, [onScrollElement]);

  const updateActiveMenuItem = React.useCallback(() => {
    const matchingMenuItem = findMatchingMenuItem(location.pathname);

    if (!matchingMenuItem) return;

    resetMenuActiveState();
    onActivateParentDropdown(matchingMenuItem);
  }, [location.pathname, onActivateParentDropdown]);

  const menuItems = React.useMemo(() => [
    // Intranet only (myPermission is modules[]): extranet keeps its own nav to the same
    // page (headerExtranetNavigation already links LOCATIONS.DASHBOARD), untouched here.
    ...(Array.isArray(myPermission) ? [{
      id: 'menu-dashboard',
      Icon: HomeIconSVG,
      label: 'Tổng quan',
      href: LOCATIONS.DASHBOARD,
    }] : []),
    {
      id: 'menu-location-list',
      Icon: BriefCaseIconSVG,
      href: LOCATIONS.LOCATION_LIST,
      label: 'Quản lý chỗ nghỉ',
    },
    ...(currentLocation ? [{
      id: `menu-current-location-${currentLocation._id}`,
      component: (
        <li key={currentLocation?._id}>
          <Link
            to={`${LOCATIONS.LOCATION_DETAIL.DEFAULT}?place=${currentLocation?._id}`}
            className={`Location ${locationId === currentLocation?._id ? 'active' : ''}`}
          >
            <img
              alt="location-img"
              src={currentLocation.previewImage}
              className="img-thumbnail rounded-circle Location__Image"
            />

            <span className="Location__Title">{currentLocation.name}</span>
          </Link>
        </li>
      ),
    }] : []),
    {
      id: 'menu-favorite',
      Icon: HeartIconSVG,
      label: 'Danh sách yêu thích',
      href: LOCATIONS.FAVORITE_PLACE,
    },
    {
      id: 'menu-booking',
      Icon: CalendarIconSVG,
      label: 'Quản lý đặt chỗ',
      href: LOCATIONS.HOME_PAGE,
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-booking-room',
          label: 'Quản lý đặt phòng',
          href: LOCATIONS.MANAGER_BOOKING,
        },
        {
          id: 'menu-booking-flight',
          label: 'Quản lý đặt vé máy bay',
          href: ENDPOINT.MANAGER_FLIGHT_BOOKING,
        },
        {
          id: 'menu-booking-flight-setting',
          label: 'Cấu hình phí vé máy bay',
          href: ENDPOINT.MANAGER_FLIGHT_SETTING,
        },
      ],
    },
    {
      id: 'menu-customer',
      Icon: CustomerManagementIconSVG,
      label: 'Quản lý khách hàng',
      href: LOCATIONS.CUSTOMERS,
    },
    {
      id: 'menu-revenue',
      Icon: BankIconSVG,
      label: 'Quản lý doanh thu',
      href: LOCATIONS.HOME_PAGE,
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-revenue-place',
          label: 'Doanh thu chỗ nghỉ',
          href: LOCATIONS.MANAGER_REVENUE,
        },
        {
          id: 'menu-revenue-flight',
          label: 'Doanh thu vé máy bay',
          href: ENDPOINT.MANAGER_FLIGHT_REVENUE,
        },
      ],
    },
    {
      id: 'menu-reviews',
      Icon: StarIconSVG,
      label: 'Quản lý đánh giá',
      href: LOCATIONS.MANAGER_REVIEWS,
    },
    {
      id: 'menu-content',
      Icon: ImagesIconSVG,
      label: 'Quản lý nội dung',
      href: LOCATIONS.HOME_PAGE,
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-content-market',
          label: 'Danh sách thị trường',
          href: LOCATIONS.MANAGER_MARKET,
        },
        {
          id: 'menu-content-areas',
          label: 'Danh sách khu vực',
          href: LOCATIONS.LIST_OF_AREAS,
        },
      ],
    },
    {
      id: 'menu-account',
      Icon: UserIconSVG,
      href: LOCATIONS.HOME_PAGE,
      label: 'Quản lý tài khoản',
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-account-internal',
          label: 'Tài khoản nội bộ',
          href: LOCATIONS.MANAGER_INTERNAL_ACCOUNT,
        },
        {
          id: 'menu-account-accommodation',
          label: 'Tài khoản chỗ nghỉ',
          href: LOCATIONS.MANAGER_ACCOMMODATION_ACCOUNT,
        },
      ],
    },
    {
      id: 'menu-operation',
      Icon: OperationIconSVG,
      label: 'Quản lý thao tác',
      href: LOCATIONS.MANAGER_OPERATION,
    },
    {
      id: 'menu-payment',
      Icon: HandCoinsIconSVG,
      label: 'Quản lý thanh toán',
      href: LOCATIONS.MANAGER_PAYMENT,
    },
    {
      id: 'menu-coin',
      Icon: BookeseIconSVG,
      label: 'Quản lý xu',
      href: LOCATIONS.MANAGER_COIN,
    },
    {
      id: 'menu-promotion',
      Icon: PercentIconSVG,
      label: 'Quản lý khuyến mãi',
      href: LOCATIONS.MANAGER_PROMOTION,
    },
    {
      id: 'menu-tax',
      Icon: TaxIconSVG,
      label: 'Quản lý thuế',
      href: LOCATIONS.MANAGER_TAX,
    },
    {
      id: 'menu-discovery',
      Icon: DiscoverSVG,
      href: LOCATIONS.HOME_PAGE,
      label: 'Khám phá',
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-discovery-create',
          label: 'Thêm điểm khám phá',
          href: ENDPOINT.MANAGER_DISCOVERY_PLACE_CREATE,
        },
        {
          id: 'menu-discovery-list',
          label: 'Danh sách điểm khám phá',
          href: ENDPOINT.MANAGER_DISCOVERY_PLACE,
        },
        {
          id: 'menu-discovery-destination',
          label: 'Quản lý điểm đến',
          href: ENDPOINT.MANAGER_DISCOVERY_DESTINATION,
        },
        {
          id: 'menu-discovery-category',
          label: 'Quản lý hạng mục',
          href: ENDPOINT.MANAGER_DISCOVERY_CATEGORY,
        },
        {
          id: 'menu-discovery-review',
          label: 'Danh sách đánh giá',
          href: ENDPOINT.MANAGER_DISCOVERY_REVIEW,
        },
      ],
    },
    {
      id: 'menu-gift',
      Icon: GiftIconSVG,
      href: LOCATIONS.HOME_PAGE,
      label: 'Quà tặng',
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-gift-voucher',
          label: 'Quản lý chiến dịch',
          href: ENDPOINT.MANAGER_GIFT_VOUCHER,
        },
        {
          id: 'menu-gift-list',
          label: 'Danh sách quà tặng',
          href: ENDPOINT.MANAGER_GIFT_LIST,
        },
        {
          id: 'menu-gift-exchange',
          label: 'Danh sách đổi quà',
          href: ENDPOINT.MANAGER_GIFT_EXCHANGE,
        },
      ],
    },
    {
      id: 'menu-complaint',
      Icon: ComplaintIconSVG,
      label: 'Quản lý khiếu nại',
      href: LOCATIONS.HOME_PAGE,
      linkClassName: 'has-arrow',
      childrens: [
        {
          id: 'menu-complaint-receive',
          label: 'Tiếp nhận khiếu nại',
          href: LOCATIONS.MANAGER_COMPLAINT,
        },
        {
          id: 'menu-complaint-tcxh',
          label: 'Tiếp nhận phản ánh của TCXH',
          href: LOCATIONS.MANAGER_REFLECT || LOCATIONS.MANAGER_TCXH_FEEDBACK,
        },
      ],
    },
  ], [currentLocation, locationId, myPermission]);

  const closeSidebarPopups = React.useCallback(() => {
    document.querySelectorAll('#side-menu > li.sidebar-popup-open').forEach((element) => {
      element.classList.remove('sidebar-popup-open');
    });
  }, []);

  const onClickLinkItem = React.useCallback((e, item) => {
    if (item?.childrens?.length > 0 && item.linkClassName?.includes('has-arrow')) {
      e.preventDefault();

      if (window.innerWidth <= 992) {
        const li = e.currentTarget.closest('li');
        const wasOpen = li?.classList.contains('sidebar-popup-open');

        closeSidebarPopups();

        if (!wasOpen && li) {
          li.classList.add('sidebar-popup-open');
        }
      }
      return;
    }

    closeSidebarPopups();
    onClickLink(e, myPermission);
  }, [closeSidebarPopups, myPermission]);

  const renderItem = React.useCallback((item) => {
    if (item.component) {
      return <React.Fragment key={item.id}>{item.component}</React.Fragment>;
    }

    const hasPermission = checkHasPermission(myPermission, item.href);
    const isLocationModuleActive = (
      item.href === LOCATIONS.LOCATION_LIST
      && location.pathname.startsWith('/locations')
    );

    return (
      <li
        key={item.id}
        className={isLocationModuleActive ? 'mm-active' : ''}
      >
        <Link
          to={item.href}
          title={item.label}
          className={`${item.linkClassName || ''} ${!hasPermission ? 'disabled-link' : ''} ${isLocationModuleActive ? 'active' : ''}`}
          onClick={(e) => onClickLinkItem(e, item)}
        >
          {!!item.Icon && <item.Icon className="sidebar-icon" />}
          <span>{item.label}</span>
        </Link>

        {!!item.childrens?.length && (
          <ul className={`sub-menu ${isLocationModuleActive ? 'mm-show' : ''}`}>
            {item.childrens.map(renderItem)}
          </ul>
        )}
      </li>
    );
  }, [location.pathname, onClickLinkItem, myPermission]);

  React.useEffect(() => {
    const menu = new MetisMenu('#side-menu');

    return () => {
      if (menu && typeof menu.dispose === 'function') {
        menu.dispose();
      }
    };
  }, [menuItems]);

  React.useEffect(() => {
    updateActiveMenuItem();
  }, [updateActiveMenuItem, currentLocation?._id]);

  React.useEffect(() => {
    containerRef.current.recalculate();
  }, [containerRef]);

  React.useEffect(() => {
    closeSidebarPopups();
  }, [closeSidebarPopups, location.pathname]);

  React.useEffect(() => {
    const onDocumentClick = (event) => {
      if (window.innerWidth > 992) return;

      const sideMenu = document.getElementById('side-menu');
      if (!sideMenu?.contains(event.target)) {
        closeSidebarPopups();
      }
    };

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, [closeSidebarPopups]);

  return (
    <SimpleBar className="h-100" ref={containerRef}>
      <div id="sidebar-menu">
        <ul className="metismenu list-unstyled" id="side-menu">
          {menuItems.map(renderItem)}
        </ul>
      </div>
    </SimpleBar>
  );
};

SidebarContent.propTypes = {
  locationList: PropTypes.instanceOf(Array),
  placeDetail: PropTypes.instanceOf(Object),
  myPermission: PropTypes.instanceOf(Object),
};

SidebarContent.defaultProps = {
  locationList: [],
  placeDetail: null,
  myPermission: {},
};

export default memo(SidebarContent);
